import {Injectable} from '@angular/core';
import {GetVisualDoorsFilterOptionsGQL, VisualDoorFilterOptions,} from '../../../generated/graphql';
import {BehaviorSubject, distinctUntilChanged, map, Observable} from 'rxjs';
import {
  CustomAttributesFilter,
  FilterService,
  GenericFilter, GenericFilterCategory,
  mapGraphqlFilterToGenericFilter,
  SortingOption,
  transformGenericFilterIntoFilterInput
} from './filter.service';
import {flatten} from "cordova-res/dist/utils/array";
import {CustomerService} from "../customer/customer.service";
import {Product} from "../../models/product";
import {FiltersPerCustomer} from "./custom-attribute-filter-options-configuration";
import {CustomerServiceImpl} from "../customer/customer.service.impl";
import {isMobile} from "../../utils/device.utils";

@Injectable({providedIn: 'root'})
export class GenericFilterService extends FilterService {

  isReferralAlreadySet = false;

  appliedFilter$: BehaviorSubject<GenericFilter> = new BehaviorSubject<GenericFilter>({
    selectedProductType: 'door'
  });

  checkForWrongDoorTypes(): boolean {
    return true;
  }

  availableFilter = new BehaviorSubject<GenericFilter | undefined>(undefined);

  constructor(
    override readonly customerService: CustomerService,
    private getVisualDoorsFilterOptionsGQL: GetVisualDoorsFilterOptionsGQL) {
    super(customerService)

    // Wait for customer config and then set the applied filter
    this.customerService.getCustomerConfig$().subscribe({
      next: config => {
        this.setFilter({
          selectedProductType: 'door'
        })
      }
    })
  }

  loadFilterOptions(previousFilter: GenericFilter): void {
    const filterInput = transformGenericFilterIntoFilterInput(previousFilter);

    this.getVisualDoorsFilterOptionsGQL.fetch({
      filter: {
        ...filterInput,
        doorTypeId: previousFilter.selectedDoorType?.id,
      }
    }, {fetchPolicy: 'no-cache'}).subscribe(response => {

      let mappedFilter = mapGraphqlFilterToGenericFilter({
        graphQLFilterOptions: response.data?.getVisualDoorsFilterOptions,
        previousFilter,
      })

      const config = this.customerService.getCustomerConfig()

      // TODO: Custom attribute filters are not yet part of the backend filter options
      // But at least the client should think they are, therefore we add them here
      if (config?.showCustomAttributesFilter) {
        const customAttributesFilterOptions = FiltersPerCustomer.hasOwnProperty(CustomerServiceImpl.GetCustomerKey()) ? FiltersPerCustomer[CustomerServiceImpl.GetCustomerKey()] : undefined

        if (customAttributesFilterOptions) {
          if (mappedFilter) {
            mappedFilter.customAttributesFilterOptions = customAttributesFilterOptions
          } else {
            mappedFilter = {
              customAttributesFilterOptions,
            }
          }
        }
      }

      this.availableFilter.next(mappedFilter);
    });
  }

  getFilter(): GenericFilter {
    return this.appliedFilter$.getValue();
  }

  getFilter$(): Observable<GenericFilter> {
    return this.appliedFilter$.pipe(distinctUntilChanged((a, b) => {
      const result = a.selectedDoorType === b.selectedDoorType &&
        a.selectedSortingOptionId === b.selectedSortingOptionId &&
        a.sortingOptions === b.sortingOptions &&
        a.onlyFavorites === b.onlyFavorites &&
        a.selectedProductType === b.selectedProductType &&
        a.referralVisualDoorExternalId === b.referralVisualDoorExternalId &&
        this.arraysEqual(this.transformCustomAttributes(a.customAttributes), this.transformCustomAttributes(b.customAttributes)) &&
        this.arraysEqual(this.transformSelectedCategoryOptions(a.categories), this.transformSelectedCategoryOptions(b.categories))
      return result
    }));
  }

  private transformSelectedCategoryOptions(categories?: Array<GenericFilterCategory>): Array<string> {
    return flatten((categories || []).map(c => c.options.filter(o => o.selected).map(o => `${c.id}-${o.id}`)))
  }

  private transformCustomAttributes(customAttributes: Array<CustomAttributesFilter> | undefined | null): Array<string> {
    return customAttributes?.map(c => `${c.attributeName}-${c.values.join(',')}`) || ['undefined']
  }

  private arraysEqual(a: Array<string>, b: Array<string>) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx])
  }

  setFilter(newFilter: Partial<GenericFilter>): void {
    this.appliedFilter$.next({
      ...this.appliedFilter$.getValue(),
      ...newFilter,
    });
  }

  isFilterFeatureEnabled$(): Observable<boolean> {

    // TODO: Currently the custom attribute filter is the only filter for overheaddoor and wayne-dalton
    // We show the custom attribute filter on desktop already directly above the door panel
    // Therefore we only need to enable the generic filter on mobile there
    // I know that's all not ideal but i couldn't come up with a better solution (yet)
    const customerKey = CustomerServiceImpl.GetCustomerKey().toLowerCase()
    if (['overheaddoor', 'wayne-dalton'].includes(customerKey)) {
      return this.availableFilter.pipe(map(v => !this.isFilterEmpty(v) && isMobile()))
    }

    return this.availableFilter.pipe(map(v => !this.isFilterEmpty(v)));
  }

  /**
   * Determines if the filter has any filter options which make them show worthy
   */
  private isFilterEmpty(v: GenericFilter | undefined): boolean {
    if (!v) return true
    return (v.categories === undefined || v.categories.length === 0) &&
      (v.heroFilterOptions === undefined || v.heroFilterOptions.length === 0) &&
      (v.sortingOptions === undefined || v.sortingOptions.length === 0) &&
      (v.doorTypeOptions === undefined || v.doorTypeOptions.length === 0) &&
      (v.customAttributesFilterOptions === undefined || v.customAttributesFilterOptions.length === 0)
  }

  getFilterOptions$(): Observable<GenericFilter | undefined> {
    return this.availableFilter;
  }

  getFilterOptionsValue(): GenericFilter | undefined {
    return this.availableFilter.getValue();
  }

  setReferralDoor(door: Product | undefined): void {

    if (door) {
      const currentFilter = {...this.appliedFilter$.getValue()}

      if (this.appliedFilter$.getValue().selectedDoorType?.id !== door.doorType.id) {
        currentFilter.selectedDoorType = door.doorType
      }

      if (!this.isReferralAlreadySet) {
        currentFilter.referralVisualDoorExternalId = door.externalIds[0]
        this.setFilter(currentFilter)
      }
    }
    this.isReferralAlreadySet = true;
  }

  loadSortingOptions(): void {
    this.loadFilterOptions(this.appliedFilter$.getValue());
  }

  getSortingOptions$(): Observable<Array<SortingOption>> {
    return this.availableFilter.pipe(map(v => v?.sortingOptions || []));
  }

  getSelectedSorting$(): Observable<string | undefined> {
    return this.appliedFilter$.pipe(map(v => v?.selectedSortingOptionId));
  }
}
